/**
 * PESTEL — six factors, always six, laid out as one glanceable grid.
 *
 * A category with no supported evidence renders as `Insufficient evidence`, not
 * as absent and not as neutral. Those three are different claims: absent says we
 * did not consider it, neutral says we considered it and found nothing pushing
 * either way, and insufficient evidence says we looked and could not tell. Only
 * the third is true, so it is the only one this component can produce — the
 * grid is built from the fixed six rather than from what came back, and the
 * insufficient tile is GREY rather than borrowing the reassuring colour.
 *
 * THE REJECTED LAYOUT was six long stacked cards behind a row of filter chips:
 * the filters were the first thing on screen, and the six postures — the actual
 * finding — could not be seen together without scrolling past all of them. The
 * grid restores the comparison; the filters are now a compact secondary control
 * inside the grid's own header.
 *
 * POINTS TO CONSIDER ARE NOT FORECASTS. Each one is a question, a signal to
 * watch, or an action that holds up across plausible futures, and each is
 * labelled as such. The moment one of them asserts an event, the section has
 * become scenario prediction, which this product does not do.
 */

import React, { useMemo, useState } from 'react';
import { ScrollView, Text, View } from 'react-native';
import { Cpu, Landmark, Leaf, Scale, TrendingUp, Users } from 'lucide-react-native';
import AnimatedPressable from '@/components/AnimatedPressable';
import { SectionDivider } from '@/components/SectionDivider';
import { useAnalysisVisuals, useIsWideLayout, type EvidenceTone } from './analysis-visuals';
import { claimFor } from './claim-join';
import {
  AnalysisHero,
  DetailCard,
  Footnote,
  LabelledBlock,
  RowDivider,
  SectionCard,
  SectionHeading,
} from './AnalysisPrimitives';
import type {
  ClaimView,
  PestelBody,
  PestelCategoryName,
  PestelItem,
  PestelPosture,
} from '@/lib/strategic-analysis/types';
import {
  DIRECTION_LABELS,
  HORIZON_LABELS,
  MATERIALITY_LABELS,
  PESTEL_CATEGORY_LABELS,
  PESTEL_CATEGORY_ORDER,
  POINT_KIND_LABELS,
  POSTURE_LABELS,
  freshnessWarning,
} from './framework-labels';

const CATEGORY_ICONS: Record<PestelCategoryName, React.ComponentType<{ size?: number; color?: string }>> = {
  political: Landmark,
  economic: TrendingUp,
  social: Users,
  technological: Cpu,
  environmental: Leaf,
  legal: Scale,
};

/** `insufficient_evidence` is grey. It is not a mild positive. Exported so that
 *  rule is assertable without inspecting a rendered style. */
export const POSTURE_TONE: Record<PestelPosture, EvidenceTone> = {
  favorable: 'positive',
  adverse: 'adverse',
  mixed: 'caution',
  insufficient_evidence: 'unknown',
};

interface PestelAnalysisProps {
  body: PestelBody;
  claims: ClaimView[];
  onInspectClaim?: (claim: ClaimView) => void;
  /** Injected so freshness is deterministic in tests. */
  now?: number;
}

type ResolvedCategory = {
  category: PestelCategoryName;
  posture: PestelPosture;
  items: PestelItem[];
};

export function PestelAnalysis({ body, claims, onInspectClaim, now = Date.now() }: PestelAnalysisProps) {
  const v = useAnalysisVisuals();
  const [horizonFilter, setHorizonFilter] = useState<PestelItem['horizon'] | null>(null);
  const [jurisdictionFilter, setJurisdictionFilter] = useState<string | null>(null);

  const jurisdictions = useMemo(() => {
    const all = body.categories.flatMap((c) => c.items.map((i) => i.jurisdiction));
    return [...new Set(all)].sort();
  }, [body]);

  // Built from the FIXED six, so a category the research did not reach is still
  // on screen with an honest posture.
  const categories: ResolvedCategory[] = PESTEL_CATEGORY_ORDER.map((category) => {
    const found = body.categories.find((c) => c.category === category);
    return {
      category,
      posture: found?.posture ?? ('insufficient_evidence' as const),
      items: found?.items ?? [],
    };
  });

  // Deterministic, and not a judgement: the first category in canonical order
  // that actually has evidence. Opening on an empty tile would make the
  // analysis look empty.
  const [selected, setSelected] = useState<PestelCategoryName>(
    () => categories.find((c) => c.items.length > 0)?.category ?? PESTEL_CATEGORY_ORDER[0],
  );
  const current = categories.find((c) => c.category === selected) ?? categories[0];

  const matches = (item: PestelItem) =>
    (!horizonFilter || item.horizon === horizonFilter) &&
    (!jurisdictionFilter || item.jurisdiction === jurisdictionFilter);

  const tally = useMemo(() => summarisePostures(categories), [categories]);

  return (
    <View className="gap-4">
      {/* The conclusion is the distribution of postures, which is a count of
          validated fields — there is no overall-outlook field in the model, and
          no headline is written to fill the slot the mockup has for one. */}
      <AnalysisHero
        label="Macro environment"
        headline={tally}
        body="Six external factors, each carrying the posture its retained evidence supports."
        accessibilityLabel={`Macro environment. ${tally}.`}
      />

      <SectionDivider label="External forces" marginTop={4} marginBottom={0} />
      <View
        className="rounded-3xl p-3 gap-3"
        style={{
          backgroundColor: v.chart.surface,
          borderWidth: 1,
          borderColor: v.chart.border,
          overflow: 'hidden',
        }}
      >
        <View className="px-1">
          <SectionHeading title="External forces" note="Impact on this venture" />
        </View>

        {/* Secondary, and compact: one scrolling row of small chips rather than
            the two stacked chip blocks that used to open the screen. */}
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          style={{ flexGrow: 0 }}
          contentContainerStyle={{ gap: 6, paddingHorizontal: 2 }}
        >
          <FilterChip label="All horizons" active={horizonFilter === null} onPress={() => setHorizonFilter(null)} />
          {(['current', '0_to_2_years', '3_to_5_years'] as const).map((horizon) => (
            <FilterChip
              key={horizon}
              label={HORIZON_LABELS[horizon]}
              active={horizonFilter === horizon}
              onPress={() => setHorizonFilter(horizon)}
            />
          ))}
          {jurisdictions.length > 1 && (
            <>
              <View style={{ width: 1, marginHorizontal: 2, backgroundColor: v.colors.border.light }} />
              <FilterChip
                label="All jurisdictions"
                active={jurisdictionFilter === null}
                onPress={() => setJurisdictionFilter(null)}
              />
              {jurisdictions.map((jurisdiction) => (
                <FilterChip
                  key={jurisdiction}
                  label={jurisdiction}
                  active={jurisdictionFilter === jurisdiction}
                  onPress={() => setJurisdictionFilter(jurisdiction)}
                />
              ))}
            </>
          )}
        </ScrollView>

        {/* 2×3 on a phone, 3×2 on a wide screen. All six visible together, which
            is the whole point of the framework. */}
        <View className="flex-row flex-wrap" style={{ gap: 8 }}>
          {categories.map((category) => (
            <FactorTile
              key={category.category}
              category={category}
              selected={selected === category.category}
              onSelect={() => setSelected(category.category)}
            />
          ))}
        </View>
      </View>

      {current && (
        <CategoryDetail
          category={current}
          claims={claims}
          onInspectClaim={onInspectClaim}
          now={now}
          matches={matches}
          filtered={horizonFilter !== null || jurisdictionFilter !== null}
        />
      )}

      {body.pointsToConsider.length > 0 && (
        <>
          <SectionDivider label="Points to consider" marginTop={4} marginBottom={4} />
          <SectionCard>
            <Text className="text-[13px] leading-5" style={{ color: v.colors.text.secondary }}>
              Questions to ask, signals to watch, and actions that hold up across plausible futures.
              None of these is a prediction.
            </Text>
            {body.pointsToConsider.map((point, index) => (
              <View key={`point-${index}`} className="gap-1">
                <RowDivider />
                {/* The kind is the guardrail: a question is not a forecast, and
                    a robust action is one that holds whichever way things go. */}
                <Text
                  className="pt-3 text-[11px] font-semibold uppercase tracking-wider"
                  style={{ color: v.colors.primary[700] }}
                >
                  {POINT_KIND_LABELS[point.kind]}
                </Text>
                <Text className="text-[13px] leading-5" style={{ color: v.colors.text.primary }}>
                  {point.text}
                </Text>
              </View>
            ))}
          </SectionCard>
        </>
      )}

      <Footnote>
        Postures and horizons are analytical estimates from retained public sources. They are not
        legal advice, and no item here predicts an event.
      </Footnote>
    </View>
  );
}

/** "1 adverse · 1 mixed · 4 insufficient evidence" — a count, not a verdict. */
function summarisePostures(categories: ResolvedCategory[]): string {
  const order: PestelPosture[] = ['favorable', 'mixed', 'adverse', 'insufficient_evidence'];
  const counts = new Map<PestelPosture, number>();
  for (const c of categories) counts.set(c.posture, (counts.get(c.posture) ?? 0) + 1);
  return order
    .filter((posture) => (counts.get(posture) ?? 0) > 0)
    .map((posture) => `${counts.get(posture)} ${POSTURE_LABELS[posture].toLowerCase()}`)
    .join(' · ');
}

/**
 * The horizon a tile shows.
 *
 * The distinct horizons its items actually carry — never a single one picked
 * out of several, which would hide that the category spans them.
 */
function tileHorizon(items: PestelItem[]): string | null {
  const distinct = [...new Set(items.map((i) => i.horizon))];
  if (distinct.length === 0) return null;
  if (distinct.length === 1) return HORIZON_LABELS[distinct[0]];
  return 'Multiple horizons';
}

function FactorTile({
  category,
  selected,
  onSelect,
}: {
  category: ResolvedCategory;
  selected: boolean;
  onSelect: () => void;
}) {
  const v = useAnalysisVisuals();
  const wide = useIsWideLayout();
  const Icon = CATEGORY_ICONS[category.category];
  const tone = v.tone(POSTURE_TONE[category.posture]);
  const horizon = tileHorizon(category.items);

  return (
    <View
      className="rounded-2xl overflow-hidden"
      style={{
        // 2-up on a phone, 3-up on a wide screen — never one long column.
        flexGrow: 1,
        flexBasis: wide ? '30%' : '46%',
        minWidth: 132,
        minHeight: 118,
        backgroundColor: selected ? v.colors.primary[50] : v.plate.background,
        borderWidth: selected ? 1.5 : 1,
        borderColor: selected ? v.colors.primary[600] : v.colors.border.light,
      }}
    >
      <AnimatedPressable
        onPress={onSelect}
        accessibilityRole="button"
        accessibilityState={{ selected }}
        accessibilityLabel={`${PESTEL_CATEGORY_LABELS[category.category]}: ${POSTURE_LABELS[category.posture]}, ${category.items.length} item(s).`}
        className="p-3 gap-2"
        style={{ flex: 1, minHeight: 118 }}
      >
        <View
          className="items-center justify-center rounded-xl"
          style={{ width: 30, height: 30, backgroundColor: v.colors.primary[50] }}
        >
          <Icon size={16} color={v.colors.primary[700]} />
        </View>

        <Text numberOfLines={1} className="text-[13px] font-semibold" style={{ color: v.colors.text.primary }}>
          {PESTEL_CATEGORY_LABELS[category.category]}
        </Text>

        {/* The posture gets its OWN row. Sharing a row with the icon truncated
            it to "Insufficient evi…" on a 2-up phone grid, and that label is
            precisely the one that must never be cut. */}
        <View className="flex-row flex-wrap" style={{ gap: 4 }}>
          <View className="rounded-full px-2 py-0.5" style={{ backgroundColor: tone.back }}>
            <Text numberOfLines={1} className="text-[9px] font-semibold" style={{ color: tone.fore }}>
              {POSTURE_LABELS[category.posture]}
            </Text>
          </View>
          {horizon ? (
            <View className="rounded-full px-2 py-0.5" style={{ backgroundColor: v.colors.surface.secondary }}>
              <Text numberOfLines={1} className="text-[9px] uppercase tracking-wider" style={{ color: v.colors.text.tertiary }}>
                {horizon}
              </Text>
            </View>
          ) : null}
        </View>
      </AnimatedPressable>
    </View>
  );
}

// ---------------------------------------------------------------------------
// The selected factor
// ---------------------------------------------------------------------------

function CategoryDetail({
  category,
  claims,
  onInspectClaim,
  now,
  matches,
  filtered,
}: {
  category: ResolvedCategory;
  claims: ClaimView[];
  onInspectClaim?: (claim: ClaimView) => void;
  now: number;
  matches: (item: PestelItem) => boolean;
  filtered: boolean;
}) {
  const v = useAnalysisVisuals();
  const tone = v.tone(POSTURE_TONE[category.posture]);
  const visible = category.items.filter(matches);

  // Grouped by the item's own validated `direction`. Nothing is reclassified
  // here, and an "unclear" item is never quietly counted as an opportunity.
  const favourable = visible.filter((i) => i.direction === 'opportunity');
  const adverse = visible.filter((i) => i.direction === 'risk');
  const undecided = visible.filter((i) => i.direction === 'mixed' || i.direction === 'unclear');

  return (
    <DetailCard
      title={PESTEL_CATEGORY_LABELS[category.category]}
      badge={POSTURE_LABELS[category.posture]}
      badgeTone={tone}
      accessibilityLabel={`Selected factor: ${PESTEL_CATEGORY_LABELS[category.category]}, ${POSTURE_LABELS[category.posture]}`}
    >
      {category.items.length === 0 && (
        <Text className="text-[13px] leading-5" style={{ color: v.colors.text.secondary }}>
          No applicable, source-backed development was found for this category. That is a gap in
          the evidence, not a finding that nothing is happening.
        </Text>
      )}

      {category.items.length > 0 && visible.length === 0 && filtered && (
        <Text className="text-[13px] leading-5" style={{ color: v.colors.text.tertiary }}>
          No items match the current filter.
        </Text>
      )}

      {favourable.length > 0 && (
        <ItemGroup
          label="Favourable implications"
          tone={v.tone('positive').fore}
          items={favourable}
          category={category.category}
          claims={claims}
          onInspectClaim={onInspectClaim}
          now={now}
        />
      )}
      {adverse.length > 0 && (
        <ItemGroup
          label="Adverse implications"
          tone={v.tone('adverse').fore}
          items={adverse}
          category={category.category}
          claims={claims}
          onInspectClaim={onInspectClaim}
          now={now}
        />
      )}
      {undecided.length > 0 && (
        <ItemGroup
          label="Mixed or unclear"
          tone={v.colors.text.tertiary}
          items={undecided}
          category={category.category}
          claims={claims}
          onInspectClaim={onInspectClaim}
          now={now}
        />
      )}
    </DetailCard>
  );
}

function ItemGroup({
  label,
  tone,
  items,
  category,
  claims,
  onInspectClaim,
  now,
}: {
  label: string;
  tone: string;
  items: PestelItem[];
  category: PestelCategoryName;
  claims: ClaimView[];
  onInspectClaim?: (claim: ClaimView) => void;
  now: number;
}) {
  const v = useAnalysisVisuals();

  return (
    <LabelledBlock label={label} tone={tone}>
      <View className="gap-2">
        {items.map((item, index) => {
          const stale = freshnessWarning(category, item.publishedAt, now);
          // Per ITEM, by its own key. This compared the item's summary against a
  // CATEGORY-level claim whose statement is "The legal posture is adverse, from
  // 3 supported item(s)." — two strings that can never be equal, so no PESTEL
  // item has ever had a route to its sources.
  const claim = claimFor(claims, item.claimKey);
          return (
            <View
              key={`${category}-item-${index}`}
              className="rounded-2xl p-3 gap-2"
              style={{ backgroundColor: v.colors.surface.secondary, overflow: 'hidden' }}
            >
              <Text className="text-[13px] leading-5" style={{ color: v.colors.text.primary }}>
                {item.summary}
              </Text>

              <View className="flex-row flex-wrap gap-1.5">
                <Chip label={item.jurisdiction} />
                <Chip label={DIRECTION_LABELS[item.direction]} />
                <Chip label={MATERIALITY_LABELS[item.materiality]} />
                <Chip label={HORIZON_LABELS[item.horizon]} />
                {item.sourceAuthority === 'unverified' && <Chip label="Source not verified as official" caution />}
                {item.publishedAt && <Chip label={`Published ${item.publishedAt.slice(0, 10)}`} />}
                {item.effectiveAt && <Chip label={`Effective ${item.effectiveAt.slice(0, 10)}`} />}
              </View>

              <View className="gap-0.5">
                <Text className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: v.colors.text.primary }}>
                  Affects
                </Text>
                <Text className="text-[13px] leading-5" style={{ color: v.colors.text.secondary }}>
                  {item.affectedAssumption}
                </Text>
              </View>

              {stale && (
                <Text className="text-[12px] leading-4" style={{ color: v.colors.amber.main }}>
                  {stale}
                </Text>
              )}

              {claim && onInspectClaim && (
                <Text
                  accessibilityRole="button"
                  accessibilityLabel={`View sources for ${PESTEL_CATEGORY_LABELS[category]}`}
                  onPress={() => onInspectClaim(claim)}
                  className="text-[13px] font-semibold"
                  style={{ color: v.colors.primary[700] }}
                >
                  View sources
                </Text>
              )}
            </View>
          );
        })}
      </View>
    </LabelledBlock>
  );
}

function FilterChip({
  label,
  active,
  onPress,
}: {
  label: string;
  active: boolean;
  onPress: () => void;
}) {
  const v = useAnalysisVisuals();
  return (
    <View
      className="rounded-full overflow-hidden"
      style={{ height: 30, backgroundColor: active ? v.colors.primary[600] : v.colors.surface.secondary }}
    >
      <AnimatedPressable
        onPress={onPress}
        accessibilityRole="button"
        accessibilityState={{ selected: active }}
        accessibilityLabel={label}
        hitSlop={{ top: 7, bottom: 7 }}
        className="items-center justify-center px-3"
        style={{ flex: 1, minHeight: 30 }}
      >
        <Text
          numberOfLines={1}
          className="text-[11px] font-semibold"
          style={{ color: active ? v.colors.text.inverse : v.colors.text.secondary }}
        >
          {label}
        </Text>
      </AnimatedPressable>
    </View>
  );
}

/**
 * `caution` marks a claim the reader must weigh differently — today, a legal or
 * political item whose source this system could not confirm is official. It
 * publishes rather than being deleted, so the caveat has to be visible beside
 * it; a flag nobody renders is the same as no flag.
 */
function Chip({ label, caution = false }: { label: string; caution?: boolean }) {
  const v = useAnalysisVisuals();
  return (
    <View
      className="rounded-full px-2 py-1"
      style={{ backgroundColor: caution ? v.tone('caution').back : v.colors.surface.primary }}
    >
      <Text
        numberOfLines={1}
        className="text-[11px]"
        style={{ color: caution ? v.tone('caution').fore : v.colors.text.secondary }}
      >
        {label}
      </Text>
    </View>
  );
}
